import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Button,
    Dialog,
    TextField,
    IconButton,
    Paper,
    CircularProgress
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import config from './config.json';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { stringify } from 'query-string';
import { saveTokens } from './utils/saveTokens';
import notify from './components/CustomUseNotify';


const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1),
        border: '1px solid #e0e0e0',
        marginBottom: theme.spacing(2),
    },
    configureButton: {
        marginLeft: 'auto',
    },
    notificationRow: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1),
        borderBottom: '1px solid #e0e0e0',
    },
    new489NotificationButton: {
        marginLeft: 'auto',
        color: theme.palette.primary.main,
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    sectionTitle: {
        marginBottom: theme.spacing(2),
    },
}));

const GracePeriodConfig = () => {
    const classes = useStyles();

    const [options, setOptions] = useState([]);
    const [autovalue, setAutoValue] = React.useState("");
    const [inputValue, setInputValue] = React.useState('');
    const [accessKeyId, setAccessKeyId] = React.useState('');

    // State for Grace Period
    const [gracePeriod, setGracePeriod] = useState({
        id: "",
        duration: "",
        startTime: "",
        endTime: "",
    });
    const [openGraceDialog, setOpenGraceDialog] = useState(false);
    const [gracePeriodConfigLoader, setGracePeriodConfigLoader] = useState(false)
    const [graceFormData, setGraceFormData] = useState({
        id: '',
        duration: '',
        startTime: '',
        endTime: '',
    });

    // State for Notifications
    const [notifications, setNotifications] = useState([]);
    const [openNotificationDialog, setOpenNotificationDialog] = useState(false);
    const [notificationLoader, setNotificationLoader] = useState(false)

    const [isEditMode, setIsEditMode] = useState(false);
    const [notificationFormData, setNotificationFormData] = useState({
        title: '',
        message: '',
        triggerAfter: '',
    });

    // Grace Period Handlers
    const handleOpenGraceDialog = () => {
        setOpenGraceDialog(true);
    };

    const handleCloseGraceDialog = () => {
        setOpenGraceDialog(false);
        setGraceFormData({
            duration: gracePeriod.duration || '',
            startTime: gracePeriod.startTime || '',
            endTime: gracePeriod.endTime || '',
        });
    };

    const handleGraceInputChange = (e) => {
        const { name, value } = e.target;
        setGraceFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSaveGraceChanges = async () => {
        try {
            let params = {
                duration: graceFormData.duration,
                start_time: graceFormData.startTime,
                end_time: graceFormData.endTime,
            }

            if (!gracePeriod.id) params.site_id = accessKeyId
            else params.id = gracePeriod.id

            const response = await fetch(`${config.HOST_NAME}/dashboard/v1/grace-period/configs`, {
                method: gracePeriod.id ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                    'RefreshToken': `Bearer ${sessionStorage.getItem('refresh_token')}`
                },
                body: JSON.stringify(params),
            });

            const updatedData = await response.json();
            if (updatedData.success) {
                setGracePeriod({
                    id: updatedData.id,
                    duration: updatedData.duration,
                    startTime: updatedData.startTime,
                    endTime: updatedData.endTime,
                });
                setOpenGraceDialog(false);
                fetchConfigurations();
            } else if (!updatedData.success) notify(updatedData.message, "warning")
        } catch (error) {
            console.error('Error saving grace period:', error);
            notify("Error saving grace period", "warning")
        }
    };

    // Notification Handlers
    const handleOpenCreateNotificationDialog = () => {
        setIsEditMode(false);
        setNotificationFormData({ grace_period_config_id: gracePeriod.id, title: '', text: '', duration: '' });
        setOpenNotificationDialog(true);
    };

    const handleOpenEditNotificationDialog = (notification) => {
        setIsEditMode(true);
        setNotificationFormData({
            id: notification.id,
            title: notification.title,
            duration: notification.duration,
            text: notification.text,
        });
        setOpenNotificationDialog(true);
    };

    const handleCloseNotificationDialog = () => {
        setOpenNotificationDialog(false);
    };

    const handleNotificationInputChange = (e) => {
        const { name, value } = e.target;
        setNotificationFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSaveNotification = async () => {
        try {
            const method = isEditMode ? 'PUT' : 'POST';
            const url = isEditMode
                ? `${config.HOST_NAME}/dashboard/v1/grace-period/messages`
                : `${config.HOST_NAME}/dashboard/v1/grace-period/messages`;

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                    'RefreshToken': `Bearer ${sessionStorage.getItem('refresh_token')}`
                },
                body: JSON.stringify(notificationFormData),
            });
            const updatedNotification = await response.json();
            if (updatedNotification.success) {
                fetchNotifications(gracePeriod.id)
                handleCloseNotificationDialog();
            } else if (!updatedNotification.success) notify(updatedNotification.message, "warning")

        } catch (error) {
            console.error('Error saving notification:', error);
            notify("Error saving notification", "warning")
        }
    };

    const handleDeleteNotification = async (id) => {
        try {
            const response = await fetch(`${config.HOST_NAME}/dashboard/v1/grace-period/messages/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                    'RefreshToken': `Bearer ${sessionStorage.getItem('refresh_token')}`
                },
            });

            const deleteNotification = await response.json();
            if (deleteNotification.success) {
                fetchNotifications(gracePeriod.id)
            } else if (!deleteNotification.success) return notify(deleteNotification.message, "warning");
        } catch (error) {
            console.error('Error deleting notification:', error);
            notify('Error deleting notification', "warning")
        }
    };

    const fetchParkings = () => {
        const token = sessionStorage.getItem('token');
        if (token != null && token !== "") {
            var params = {
                user_id: sessionStorage.getItem("user_id"),
                filter: JSON.stringify({ name: inputValue ? inputValue : undefined }),
                sort: JSON.stringify(["id", "ASC"]),
                page: 1,
                perPage: 10
            }
            fetch(`${config.HOST_NAME}/crm/v3/uniqueParkingAccessKeys?${stringify(params)}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                    'RefreshToken': `Bearer ${sessionStorage.getItem('refresh_token')}`
                },
            })
                .then(response => {
                    saveTokens(response)
                    return response.json()
                })
                .then((responseJson) => {
                    if (responseJson.success) {
                        let data = responseJson.data
                        let results = []
                        for (let val in data) {
                            results.push({ accessKeyId: data[val].id, accessKey: data[val].access_key, name: data[val].name })
                        }
                        setOptions([...results])

                    } else {
                        notify(responseJson.message, 'warning');
                    }
                })
                .catch((e) => {
                    notify('Error fetching data', 'warning');
                    console.log(e);
                });
        }
    };

    const fetchConfigurations = async () => {
        try {
            setGracePeriodConfigLoader(true)

            const graceResponse = await fetch(`${config.HOST_NAME}/dashboard/v1/grace-period/${accessKeyId}/configs`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                    'RefreshToken': `Bearer ${sessionStorage.getItem('refresh_token')}`
                },
            });
            const graceData = await graceResponse.json();
            if (graceData.success && graceData.data && graceData.data.id) {
                setGracePeriod({
                    id: graceData?.data?.id || "",
                    duration: graceData?.data?.duration || "",
                    startTime: graceData?.data?.start_time || "",
                    endTime: graceData?.data?.end_time || "",
                });
                setGraceFormData({
                    id: graceData?.data?.id || "",
                    duration: graceData?.data?.duration || "",
                    startTime: graceData?.data?.start_time || "",
                    endTime: graceData?.data?.end_time || "",
                });

                if (graceData.data.id) fetchNotifications(graceData.data.id)
            }
            else if (!graceData.success) notify(graceData.message, "warning")
        } catch (error) {
            console.error('Error fetching data:', error);
            notify("Error fetching data", "warning")
        } finally {
            setGracePeriodConfigLoader(false);
        }
    }

    const fetchNotifications = async (id) => {
        try {
            setNotificationLoader(true)
            const notificationResponse = await fetch(`${config.HOST_NAME}/dashboard/v1/grace-period/${id}/messages`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                    'RefreshToken': `Bearer ${sessionStorage.getItem('refresh_token')}`
                },
            });
            const notificationData = await notificationResponse.json();
            if (notificationData.success && notificationData.data) {
                setNotifications(notificationData.data);
            } else notify(notificationData.message, "warning")

        } catch (error) {
            console.error('Error fetching data:', error);
            notify("Error fetching data", "warning")
        } finally {
            setNotificationLoader(false);
        }
    }

    const resetStates = () => {
        setGracePeriod({
            id: "",
            duration: "",
            startTime: "",
            endTime: "",
        });
        setOpenGraceDialog(false);
        setGracePeriodConfigLoader(false)
        setGraceFormData({
            id: '',
            duration: '',
            startTime: '',
            endTime: '',
        });
        setNotifications([]);
        setOpenNotificationDialog(false);
        setNotificationLoader(false)
        setIsEditMode(false);
        setNotificationFormData({
            title: '',
            message: '',
            triggerAfter: '',
        });
    }

    useEffect(() => {
        fetchParkings()
        resetStates()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue])

    useEffect(() => {
        if (accessKeyId) fetchConfigurations()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, accessKeyId]);

    return (
        <Paper elevation={1} style={{ padding: "20px", marginTop: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 350px))", columnGap: 20, width: "100%", marginBottom: 20 }}>
                <Autocomplete
                    value={autovalue}
                    onChange={(event, newValue) => {
                        setAutoValue(newValue);
                        setAccessKeyId(newValue?.accessKeyId || '')
                    }}
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue) => {
                        setInputValue(newInputValue);
                    }}
                    id="controllable-states-demo"
                    size="small"
                    options={options}
                    getOptionLabel={option => option.name}
                    style={{ marginBottom: 20, width: 350 }}
                    renderInput={(params) => <TextField {...params} label="Parking Name" variant="outlined" />}
                />
            </div>

            {!accessKeyId && <div style={{ fontSize: "14px", color: "#3D3D3D", marginBottom: "5px", fontWeight: "600" }}>Please select a parking to get configurations.</div>}

            {accessKeyId &&
                <div>
                    {/* Grace Period Section */}
                    <div style={{ padding: "24px", borderRadius: "5px", border: '1px solid #e0e0e0', marginBottom: "20px" }}>
                        <div>
                            <div>
                                <div style={{ fontSize: "14px", color: "#3D3D3D", marginBottom: "5px", fontWeight: "600" }}>Configure Grace Period {gracePeriodConfigLoader && <CircularProgress size={14} style={{ marginLeft: 5 }} />}</div>
                                {!gracePeriod.id &&
                                    <div style={{ backgroundColor: '#fff3cd', padding: "4px 8px", borderRadius: "5px", display: "inline-flex" }}>
                                        <div style={{ fontSize: "12px", color: "#462005" }}>
                                            ⚠ To activate the grace period, please set both values above
                                        </div>
                                    </div>
                                }
                                <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-start", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                                    <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: "20px" }}>
                                        <div>
                                            <div style={{ fontSize: "14px", color: "#3D3D3D" }}>Grace Period Duration</div>
                                            <div style={{ fontSize: "14px", color: "#3D3D3D", fontWeight: "600", marginTop: "10px" }}>{gracePeriod.duration ? `${gracePeriod.duration} minutes` : 'Not set'}</div>
                                        </div>
                                        <div style={{ width: "2px", height: "50px", backgroundColor: "#DCDCDC" }}></div>
                                        <div>
                                            <div style={{ fontSize: "14px", color: "#3D3D3D" }}>Start Time - End Time</div>
                                            <div style={{ fontSize: "14px", color: "#3D3D3D", fontWeight: "600", marginTop: "10px" }}>{gracePeriod.startTime && graceFormData.endTime ? `${gracePeriod.startTime} - ${gracePeriod.endTime}` : 'Not set'}</div>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        className={classes.configureButton}
                                        onClick={handleOpenGraceDialog}
                                        disabled={gracePeriodConfigLoader}
                                        style={{ textTransform: "capitalize" }}
                                    >
                                        Configure Now
                                    </Button>
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Notification Section */}
                    {gracePeriod && gracePeriod.id && <div style={{ padding: "24px", borderRadius: "5px", border: '1px solid #e0e0e0' }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                            <div style={{ fontSize: "14px", color: "#3D3D3D", marginBottom: "16px", fontWeight: "600" }}>
                                Configure Notification {notificationLoader && <CircularProgress size={14} style={{ marginLeft: 5 }} />}
                            </div>

                            <Button
                                variant="outlined"
                                color="primary"
                                className={classes.configureButton}
                                style={{ textTransform: "capitalize" }}
                                startIcon={<AddIcon />}
                                onClick={handleOpenCreateNotificationDialog}
                                disabled={!gracePeriod.id || notificationLoader}
                            >
                                New Grace Notification
                            </Button>
                        </div>

                        {notifications.length === 0 ? (
                            <div style={{ fontSize: "14px", color: "#3D3D3D" }}>
                                No notifications configured.
                            </div>
                        ) : (
                            notifications.map((notification) => (
                                <div key={notification.id} style={{ display: 'flex', justifyContent: "flex-start", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                                    <div style={{ flex: 1, display: 'flex', justifyContent: "flex-start", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                        <div style={{flex: 1, minWidth:"200px"}}>
                                            <TextField
                                                label="Title"
                                                name="title"
                                                value={notification.title}
                                                disabled
                                                size='small'
                                                variant="outlined"
                                                fullWidth
                                            />
                                        </div>
                                        <div style={{flex: 1, minWidth:"200px"}}>
                                            <TextField
                                                label="Text"
                                                name="text"
                                                value={notification.text}
                                                disabled
                                                size='small'
                                                variant="outlined"
                                                fullWidth
                                            />
                                        </div>
                                        <div style={{width:"100px"}}>
                                            <TextField
                                                label="Trigger After"
                                                name="duration"
                                                value={notification.duration}
                                                disabled
                                                size='small'
                                                variant="outlined"
                                            />
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: "flex-start", alignItems: "center", gap: 10 }}>
                                        <IconButton onClick={() => handleOpenEditNotificationDialog(notification)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleDeleteNotification(notification.id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    }
                </div>
            }

            {/* Grace Period Dialog */}
            <Dialog open={openGraceDialog} onClose={handleCloseGraceDialog}>
                <div style={{ padding: "20px" }}>
                    <div style={{ color: "#3D3D3D", marginBottom: "5px", fontWeight: "600" }}>Configure Grace Period</div>
                    <div style={{ marginTop: "20px" }}>
                        <TextField
                            type='number'
                            label="Duration in minutes"
                            name="duration"
                            value={graceFormData.duration}
                            onChange={handleGraceInputChange}
                            variant="outlined"
                            size='small'
                        />
                    </div>
                    <div style={{ marginTop: "20px" }}>
                        <TextField
                            variant="outlined"
                            label="Start Time"
                            name="startTime"
                            value={graceFormData.startTime}
                            onChange={e => {
                                handleGraceInputChange(e)
                            }}
                            size='small'
                        />
                    </div>
                    <div style={{ marginTop: "20px" }}>
                        <TextField
                            variant="outlined"
                            label="End Time"
                            name="endTime"
                            value={graceFormData.endTime}
                            onChange={e => {
                                handleGraceInputChange(e)
                            }}
                            size='small'
                        />
                    </div>

                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px", marginTop: "20px" }}>
                        <Button onClick={handleCloseGraceDialog} style={{color:"#3D3D3D"}}>
                            Cancel
                        </Button>
                        <Button disableElevation onClick={handleSaveGraceChanges} color="primary" variant="contained" style={{ textTransform: "capitalize" }}>
                            Save Changes
                        </Button>
                    </div>
                </div>
            </Dialog>

            {/* Notification Dialog */}
            <Dialog open={openNotificationDialog} onClose={handleCloseNotificationDialog} fullWidth>
                <div style={{ padding: "20px" }}>
                    <div style={{ color: "#3D3D3D", marginBottom: "5px", fontWeight: "600" }}>{isEditMode ? 'Edit Notification' : 'Configure Notification'}</div>
                    <div style={{ marginTop: "20px" }}>
                        <TextField
                            label="Title"
                            name="title"
                            value={notificationFormData.title}
                            onChange={handleNotificationInputChange}
                            variant="outlined"
                            size='small'
                            fullWidth
                        />
                    </div>
                    <div style={{ marginTop: "20px" }}>
                        <TextField
                            label="Text"
                            name="text"
                            value={notificationFormData.text}
                            onChange={handleNotificationInputChange}
                            variant="outlined"
                            size='small'
                            fullWidth
                        />
                    </div>
                    <div style={{ marginTop: "20px" }}>
                        <TextField
                            type='number'
                            label="Trigger After"
                            name="duration"
                            value={notificationFormData.duration}
                            onChange={handleNotificationInputChange}
                            variant="outlined"
                            size='small'
                        />
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px", marginTop: "20px" }}>
                        <Button onClick={handleCloseNotificationDialog} style={{color:"#3D3D3D"}}>
                            Cancel
                        </Button>
                        <Button disableElevation onClick={handleSaveNotification} color="primary" variant="contained" style={{ textTransform: "capitalize" }}>
                            Save Notification
                        </Button>
                    </div>
                </div>
            </Dialog>
        </Paper>
    );
};

export default GracePeriodConfig;